import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Collapse,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

// 样式化组件
const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#f50057',
  },
  '& .MuiRating-iconHover': {
    color: '#ff3d47',
  },
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled('img')({
  width: '100px',
  height: '100px',
  objectFit: 'cover',
  borderRadius: '4px',
  margin: '8px'
});

// 评价表单组件
const ProductReviewForm = ({ productId, onSubmit }) => {
  const { currentUser } = useAuth();
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(true);

  // 检查用户是否购买过此商品
  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if (!currentUser || !productId) {
        setCheckingPurchase(false);
        return;
      }

      try {
        const db = getFirestore();
        const ordersRef = collection(db, 'orders');
        
        // 查询用户的已完成订单
        const ordersQuery = query(
          ordersRef,
          where('userId', '==', currentUser.uid),
          where('status', '==', 'completed'),
          where('items', 'array-contains', { productId })
        );
        
        const orderSnap = await getDocs(ordersQuery);
        
        // 检查是否已经评价过
        if (!orderSnap.empty) {
          const reviewsRef = collection(db, 'reviews');
          const reviewQuery = query(
            reviewsRef,
            where('userId', '==', currentUser.uid),
            where('productId', '==', productId)
          );
          
          const reviewSnap = await getDocs(reviewQuery);
          
          // 如果没有评价记录，则可以评价
          setHasPurchased(!reviewSnap.docs.length);
        } else {
          setHasPurchased(false);
        }
      } catch (error) {
        console.error('检查购买状态失败：', error);
        setError('检查购买状态失败，请稍后再试');
      } finally {
        setCheckingPurchase(false);
      }
    };

    if (open) {
      checkPurchaseStatus();
    }
  }, [currentUser, productId, open]);

  // 处理图片上传
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    if (images.length + files.length > 5) {
      setError('最多只能上传5张图片');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    const invalidFiles = files.filter(file => 
      !allowedTypes.includes(file.type) || file.size > maxSize
    );
    
    if (invalidFiles.length > 0) {
      setError('只支持JPG、PNG和WebP格式的图片，且大小不超过5MB');
      return;
    }
    
    // 上传图片到Firebase Storage
    try {
      const storage = getStorage();
      const auth = getAuth();
      
      const uploadTasks = files.map(async (file) => {
        const fileId = Date.now() + '_' + Math.random().toString(36).substring(2, 9);
        const storageRef = ref(storage, `reviews/${auth.currentUser.uid}/${fileId}_${file.name}`);
        
        // 创建上传任务
        const uploadTask = uploadBytesResumable(storageRef, file);
        
        // 监听上传进度
        uploadTask.on('state_changed', 
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: Math.round(progress)
            }));
          },
          (error) => {
            console.error('上传图片失败：', error);
            setError('图片上传失败，请重试');
          }
        );
        
        // 等待上传完成
        await uploadTask;
        
        // 获取下载URL
        const downloadURL = await getDownloadURL(storageRef);
        
        return downloadURL;
      });
      
      // 等待所有上传任务完成
      const uploadedUrls = await Promise.all(uploadTasks);
      
      // 更新images状态
      setImages(prev => [...prev, ...uploadedUrls]);
      
      // 清除错误信息和进度
      setError('');
      setUploadProgress({});
    } catch (error) {
      console.error('处理图片上传失败：', error);
      setError('图片上传过程中出错，请重试');
    }
  };

  // 删除已上传的图片
  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // 提交评价
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('请先登录后再评价');
      return;
    }
    
    if (rating < 1) {
      setError('请选择评分');
      return;
    }
    
    if (!content.trim()) {
      setError('请输入评价内容');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await onSubmit({
        rating,
        content: content.trim(),
        images
      });
      
      if (result.success) {
        setSuccess('评价提交成功！');
        // 重置表单
        setRating(5);
        setContent('');
        setImages([]);
        setOpen(false);
      } else {
        throw new Error(result.error || '提交评价失败');
      }
    } catch (error) {
      console.error('提交评价失败：', error);
      setError(error.message || '提交评价失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 切换表单显示状态
  const handleToggleForm = () => {
    setOpen(!open);
    if (!open) {
      // 重置表单状态
      setError('');
      setSuccess('');
    }
  };

  if (!currentUser) {
    return (
      <Alert severity="info">
        请先登录后再评价
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<EditIcon />}
        onClick={handleToggleForm}
        disabled={checkingPurchase}
      >
        {open ? '取消评价' : '写评价'}
      </Button>

      <Collapse in={open}>
        {checkingPurchase ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : !hasPurchased ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            只有购买过此商品的用户才能评价
          </Alert>
        ) : (
          <Paper sx={{ p: 3, mt: 2 }}>
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
              
              <Box sx={{ mb: 3 }}>
                <Typography component="legend">您的评分</Typography>
                <StyledRating
                  name="rating"
                  value={rating}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                  }}
                  precision={1}
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarBorderIcon fontSize="inherit" />}
                />
              </Box>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="评价内容"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请分享您对商品的使用体验..."
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ mb: 2 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  sx={{ mr: 2 }}
                >
                  上传图片
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  最多5张，每张不超过5MB
                </Typography>
              </Box>
              
              {/* 图片预览 */}
              {images.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {images.map((url, index) => (
                    <Box key={url} sx={{ position: 'relative' }}>
                      <ImagePreview src={url} alt={`预览图片 ${index + 1}`} />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              
              {/* 上传进度 */}
              {Object.entries(uploadProgress).map(([id, progress]) => (
                <Box key={id} sx={{ width: '100%', mb: 1 }}>
                  <Typography variant="caption">
                    正在上传图片 {progress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={progress} />
                </Box>
              ))}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? <CircularProgress size={24} /> : '提交评价'}
                </Button>
              </Box>
            </form>
          </Paper>
        )}
      </Collapse>
    </Box>
  );
};

export default ProductReviewForm;